import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import styles from './notifs.module.css';
import { API_URL } from '../Utils/api';
import Menu from './Menu';
import { useNotifications } from '../hooks/useNotifications';
import {
  FaBell, FaHeart, FaComment,
  FaUserPlus, FaExclamationTriangle, FaSyncAlt
} from 'react-icons/fa';

/* ─── Config des types de notification ───────────────────── */
const NOTIF_CONFIG = {
  like: {
    icon : <FaHeart />,
    color: '#f4212e',
    bg   : 'rgba(249,24,128,0.12)',
  },
  comment: {
    icon : <FaComment />,
    color: '#1d9bf0',
    bg   : 'rgba(29,155,240,0.12)',
  },
  follow: {
    icon : <FaUserPlus />,
    color: '#00ba7c',
    bg   : 'rgba(0,186,124,0.12)',
  },
  default: {
    icon : <FaBell />,
    color: '#7c3aed',
    bg   : 'rgba(124,58,237,0.12)',
  },
};

const getNotifConfig = (type) =>
  NOTIF_CONFIG[type] || NOTIF_CONFIG.default;

/* ─── Helper temps ───────────────────────────────────────── */
const formatTime = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString);
  const s = Math.floor(diff / 1000);
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (s < 60)  return `${s}s`;
  if (m < 60)  return `${m}min`;
  if (h < 24)  return `${h}h`;
  if (d < 7)   return `${d}j`;
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short',
  });
};

/* ─── Composant Notifs ───────────────────────────────────── */
const Notifs = ({ refresh, setRefresh, userId, pp, active, setActive }) => {
  const navigate = useNavigate();

  const [isMobile,       setIsMobile]        = useState(false);
  const [markingAllRead, setMarkingAllRead]  = useState(false);

  const token = useMemo(() => localStorage.getItem('token'), []);

  const { data: notifications = [], isLoading: loading, isError, error: qError, refetch } = useNotifications(token, userId);
  const error = isError ? qError.message : null;

  /* ── Responsive ── */
  useEffect(() => {
    const check = () =>
      setIsMobile(/Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent) || window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (refresh) refetch();
  }, [refresh, refetch]);

  /* ── Notifs triées ── */
  const sorted = useMemo(() =>
    [...notifications].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [notifications]
  );

  // ✅ Fix : le champ est isRead (pas read)
  const unreadCount = useMemo(() =>
    sorted.filter(n => !n.isRead).length,
    [sorted]
  );

  /* ── Clic sur une notif ── */
  const handleClick = useCallback(async (notif) => {
    // Navigation selon le type
    if (notif.notifType === 'follow') {
      navigate(`/profile/${notif.userId}`);
    } else if (notif.post?._id || notif.post) {
      const postId = notif.post?._id || notif.post;
      navigate(`/post/${postId}`);
    } else if (notif.userId) {
      navigate(`/profile/${notif.userId}`);
    }

    // Marquer comme lu
    if (!notif.isRead) {
      try {
        await axios.put(`${API_URL}/notifications/read`,
          { notifId: notif._id },
          { headers: { Authorization: `Bearer${token}` } }
        );
        refetch();
      } catch (e) {
        console.error('Erreur markAsRead:', e.message);
      }
    }
  }, [navigate, token]);

  /* ── Tout marquer comme lu ── */
  const markAllAsRead = useCallback(async () => {
    if (markingAllRead || unreadCount === 0) return;
    setMarkingAllRead(true);
    try {
      const unread = notifications.filter(n => !n.isRead);
      await axios.put(`${API_URL}/notifications/read-all`,
        { notifications: unread },
        { headers: { Authorization: `Bearer${token}` } }
      );
      refetch();
    } catch (e) {
      console.error('Erreur markAllAsRead:', e.message);
    } finally {
      setMarkingAllRead(false);
    }
  }, [notifications, token, unreadCount, markingAllRead]);

  /* ── Loading ── */
  if (loading) return (
    <div className={styles.notifs}>
      <div className={styles.header}>
        <h2 className={styles.title}>Notifications</h2>
      </div>
      <div className={styles.skeletonList}>
        {[...Array(5)].map((_, i) => (
          <div key={i} className={styles.skeletonItem}>
            <div className={styles.skeletonAvatar} />
            <div className={styles.skeletonLines}>
              <div className={styles.skeletonLine} style={{ width: '60%' }} />
              <div className={styles.skeletonLine} style={{ width: '30%' }} />
            </div>
          </div>
        ))}
      </div>
      {isMobile && <Menu pp={pp} active={active} setActive={setActive} />}
    </div>
  );

  /* ── Erreur ── */
  if (error) return (
    <div className={styles.notifs}>
      <div className={styles.errorState}>
        <FaExclamationTriangle className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryBtn} onClick={() => refetch()}>Réessayer</button>
      </div>
      {isMobile && <Menu pp={pp} active={active} setActive={setActive} />}
    </div>
  );

  return (
    <>
      <div className={styles.notifs}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Notifications</h2>
            {unreadCount > 0 && (
              <span className={styles.unreadBadge}>{unreadCount}</span>
            )}
          </div>
          <div className={styles.headerActions}>
            {unreadCount > 0 && (
              <button
                className={styles.markAllBtn}
                onClick={markAllAsRead}
                disabled={markingAllRead}
              >
                {markingAllRead ? '...' : 'Tout lire'}
              </button>
            )}
            <button
              className={styles.refreshBtn}
              onClick={() => setRefresh?.(Date.now())}
              aria-label="Rafraîchir"
            >
              <FaSyncAlt />
            </button>
          </div>
        </div>

        {/* ── Liste ── */}
        <div className={styles.list}>
          {sorted.length === 0 ? (
            <div className={styles.emptyState}>
              <FaBell className={styles.emptyIcon} />
              <h3>Aucune notification</h3>
              <p>Vous serez notifié ici dès qu'il y aura du nouveau</p>
            </div>
          ) : (
            sorted.map((notif) => {
              const cfg = getNotifConfig(notif.notifType);
              return (
                <div
                  key={notif._id}
                  className={`${styles.item} ${!notif.isRead ? styles.unread : ''}`}
                  onClick={() => handleClick(notif)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleClick(notif)}
                >
                  {/* Avatar + icône type */}
                  <div className={styles.avatarWrap}>
                    <img
                      src={notif.userPP || `https://ui-avatars.com/api/?name=${notif.username}&size=48&background=f1f5f9&color=64748b`}
                      alt={notif.username}
                      className={styles.avatar}
                      onError={e => {
                        e.target.src = `https://ui-avatars.com/api/?name=${notif.username}&size=48&background=f1f5f9&color=64748b`;
                      }}
                    />
                    <span
                      className={styles.typeIcon}
                      style={{ color: cfg.color }}
                    >
                      {cfg.icon}
                    </span>
                  </div>

                  {/* Texte */}
                  <div className={styles.content}>
                    <p className={styles.message}>
                      <strong>{notif.username}</strong>
                      {' '}{notif.message}
                    </p>
                    <span className={styles.time}>{formatTime(notif.createdAt)}</span>
                  </div>

                  {/* Aperçu du post si disponible */}
                  {notif.post?.postPicture && (
                    <img
                      src={notif.post.postPicture}
                      alt=""
                      className={styles.postThumb}
                    />
                  )}

                  {/* Dot non lu */}
                  {!notif.isRead && <span className={styles.dot} />}
                </div>
              );
            })
          )}
        </div>
      </div>

      {isMobile && <Menu pp={pp} active={active} setActive={setActive} />}
    </>
  );
};

export default Notifs;